<template>
  <PartnersLayout>
    <template #header>
      <PartnersHeader
        title="리스트 광고 신청"
        subtitle="현장 목록 상단에 노출되는 광고 (무료)"
      />
    </template>

    <div class="px-4 lg:px-6 pb-6">
      <div class="max-w-6xl mx-auto">
        <div class="grid grid-cols-1 lg:grid-cols-3 gap-6">
          <!-- 왼쪽: 안내 및 바로가기 -->
          <div class="lg:col-span-1 space-y-4">
            <!-- 안내 카드 -->
            <div class="bg-white rounded-2xl border border-slate-200 p-5">
              <div class="flex items-center gap-2 mb-3">
                <div class="w-8 h-8 bg-blue-100 rounded-lg flex items-center justify-center">
                  <svg class="w-4 h-4 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"/>
                  </svg>
                </div>
                <h3 class="font-semibold text-slate-800">리스트 광고 안내</h3>
              </div>
              <ul class="text-sm text-slate-600 space-y-2">
                <li class="flex items-start gap-2">
                  <span class="text-blue-500 mt-0.5">•</span>
                  <span>광고 등록은 <strong class="text-blue-700">무료</strong>입니다</span>
                </li>
                <li class="flex items-start gap-2">
                  <span class="text-blue-500 mt-0.5">•</span>
                  <span>신청 후 <strong class="text-blue-700">관리자 승인</strong>이 필요합니다</span>
                </li>
                <li class="flex items-start gap-2">
                  <span class="text-blue-500 mt-0.5">•</span>
                  <span>광고 기간은 <strong class="text-blue-700">1주일</strong>입니다</span>
                </li>
                <li class="flex items-start gap-2">
                  <span class="text-blue-500 mt-0.5">•</span>
                  <span>계정당 <strong class="text-blue-700">1개</strong>만 등록 가능</span>
                </li>
                <li class="flex items-start gap-2">
                  <span class="text-blue-500 mt-0.5">•</span>
                  <span>광고 유입 콜은 <strong class="text-blue-700">건당 10,000P</strong>에 구매</span>
                </li>
                <li class="flex items-start gap-2">
                  <span class="text-blue-500 mt-0.5">•</span>
                  <span>현장 목록 <strong class="text-blue-700">최상단</strong>에 5개 동시 노출</span>
                </li>
              </ul>
            </div>

            <!-- 잔여 슬롯 현황 -->
            <div class="bg-white rounded-2xl border border-slate-200 p-5">
              <h3 class="font-semibold text-slate-800 mb-3">시간대별 잔여 현황</h3>
              <div v-if="loadingSlots" class="py-4 text-center text-slate-400">
                <div class="spinner-sm mx-auto mb-2"></div>
                <span class="text-sm">로딩 중...</span>
              </div>
              <div v-else class="space-y-2">
                <div
                  v-for="slot in availableSlots"
                  :key="slot.id"
                  class="flex items-center justify-between p-2 rounded-lg"
                  :class="slot.isFull ? 'bg-red-50' : 'bg-slate-50'"
                >
                  <div class="flex items-center gap-2">
                    <span>{{ slot.icon }}</span>
                    <span class="text-sm font-medium text-slate-700">{{ slot.label }}</span>
                    <span class="text-xs text-slate-400">{{ slot.start }}-{{ slot.end }}</span>
                  </div>
                  <div class="flex items-center gap-1">
                    <span
                      :class="slot.isFull ? 'text-red-600' : 'text-green-600'"
                      class="text-sm font-semibold"
                    >
                      {{ slot.available }}/5
                    </span>
                    <span class="text-xs text-slate-400">잔여</span>
                  </div>
                </div>
              </div>
            </div>

            <!-- 바로가기 -->
            <div class="bg-white rounded-2xl border border-slate-200 p-5">
              <h3 class="font-semibold text-slate-800 mb-3">바로가기</h3>
              <div class="space-y-2">
                <router-link
                  to="/partners/listad/my"
                  class="flex items-center gap-3 p-3 rounded-xl hover:bg-slate-50 transition group"
                >
                  <div class="w-9 h-9 bg-blue-100 rounded-lg flex items-center justify-center group-hover:bg-blue-200 transition">
                    <svg class="w-4 h-4 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2"/>
                    </svg>
                  </div>
                  <div class="flex-1">
                    <p class="text-sm font-medium text-slate-700">내 리스트 광고</p>
                    <p class="text-xs text-slate-500">신청 현황 확인</p>
                  </div>
                  <svg class="w-4 h-4 text-slate-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 5l7 7-7 7"/>
                  </svg>
                </router-link>
                <router-link
                  to="/partners/ad/create"
                  class="flex items-center gap-3 p-3 rounded-xl hover:bg-slate-50 transition group"
                >
                  <div class="w-9 h-9 bg-blue-100 rounded-lg flex items-center justify-center group-hover:bg-blue-200 transition">
                    <svg class="w-4 h-4 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M13.828 10.172a4 4 0 00-5.656 0l-4 4a4 4 0 105.656 5.656l1.102-1.101m-.758-4.899a4 4 0 005.656 0l4-4a4 4 0 00-5.656-5.656l-1.1 1.1"/>
                    </svg>
                  </div>
                  <div class="flex-1">
                    <p class="text-sm font-medium text-slate-700">캠페인 만들기</p>
                    <p class="text-xs text-slate-500">광고에 필요한 캠페인 생성</p>
                  </div>
                  <svg class="w-4 h-4 text-slate-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 5l7 7-7 7"/>
                  </svg>
                </router-link>
              </div>
            </div>
          </div>

          <!-- 오른쪽: 메인 콘텐츠 -->
          <div class="lg:col-span-2">
            <!-- 로딩 -->
            <div v-if="loading" class="bg-white rounded-2xl border border-slate-200 p-12 text-center">
              <div class="spinner mx-auto mb-4"></div>
              <p class="text-slate-500">데이터를 불러오는 중...</p>
            </div>

            <!-- 이미 광고가 있는 경우 -->
            <div v-else-if="hasActiveListAd" class="bg-amber-50 border border-amber-200 rounded-2xl p-6">
              <div class="flex items-start gap-3">
                <svg class="w-6 h-6 text-amber-500 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"/>
                </svg>
                <div>
                  <p class="font-semibold text-amber-800">이미 등록된 광고가 있습니다</p>
                  <p class="text-sm text-amber-600 mt-1">계정당 1개의 리스트 광고만 등록할 수 있습니다.</p>
                  <p class="text-sm text-amber-600">기존 광고가 만료되거나 삭제된 후 새로운 광고를 신청해주세요.</p>
                  <router-link
                    to="/partners/listad/my"
                    class="inline-flex items-center gap-1 mt-3 text-sm text-blue-600 font-medium hover:underline"
                  >
                    내 광고 확인하기
                    <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 5l7 7-7 7"/>
                    </svg>
                  </router-link>
                </div>
              </div>
            </div>

            <!-- 캠페인 없음 안내 -->
            <div v-else-if="campaigns.length === 0" class="bg-amber-50 border border-amber-200 rounded-2xl p-6">
              <div class="flex items-center gap-3">
                <svg class="w-6 h-6 text-amber-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"/>
                </svg>
                <div>
                  <p class="font-semibold text-amber-800">활성화된 캠페인이 없습니다</p>
                  <p class="text-sm text-amber-600">리스트 광고를 등록하려면 먼저 캠페인을 생성해주세요.</p>
                  <router-link to="/partners/ad/create" class="inline-flex items-center gap-1 mt-2 text-sm text-blue-600 font-medium hover:underline">
                    캠페인 만들기
                    <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 5l7 7-7 7"/>
                    </svg>
                  </router-link>
                </div>
              </div>
            </div>

            <div v-else class="space-y-4">
              <!-- 캠페인 선택 -->
              <div class="bg-white rounded-2xl border border-slate-200 p-6">
                <h2 class="text-lg font-semibold text-slate-800 mb-4">1. 캠페인 선택</h2>

                <div class="space-y-3">
                  <label
                    v-for="campaign in campaigns"
                    :key="campaign.id"
                    :class="[
                      'flex items-center gap-4 p-4 rounded-xl border-2 cursor-pointer transition',
                      selectedCampaign?.id === campaign.id
                        ? 'border-blue-500 bg-blue-50'
                        : 'border-slate-200 hover:border-blue-300'
                    ]"
                  >
                    <input
                      type="radio"
                      :value="campaign"
                      v-model="selectedCampaign"
                      class="w-5 h-5 text-blue-600"
                    />
                    <div class="flex-1">
                      <h3 class="font-semibold text-slate-800">{{ campaign.propertyName }}</h3>
                      <p class="text-sm text-slate-500">캠페인 코드: {{ campaign.code }}</p>
                      <p class="text-xs text-slate-400 mt-1">만료일: {{ campaign.endDate }}</p>
                    </div>
                    <span class="px-3 py-1 text-xs font-medium bg-green-100 text-green-700 rounded-full">
                      활성
                    </span>
                  </label>
                </div>
              </div>

              <!-- 시간대 선택 -->
              <div class="bg-white rounded-2xl border border-slate-200 p-6">
                <h2 class="text-lg font-semibold text-slate-800 mb-2">2. 노출 시간대 선택</h2>
                <p class="text-sm text-slate-500 mb-4">광고가 노출될 시간대를 선택하세요. (1개만 선택 가능)</p>

                <div class="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-6 gap-2">
                  <button
                    v-for="slot in availableSlots"
                    :key="slot.id"
                    type="button"
                    :disabled="slot.isFull"
                    @click="!slot.isFull && (selectedTimeSlot = slot)"
                    :class="[
                      'p-3 rounded-xl border-2 text-left transition',
                      slot.isFull
                        ? 'border-red-200 bg-red-50 cursor-not-allowed opacity-60'
                        : selectedTimeSlot?.id === slot.id
                          ? 'border-blue-500 bg-blue-50 ring-2 ring-blue-500/20'
                          : 'border-slate-200 hover:border-blue-300 hover:bg-blue-50/50'
                    ]"
                  >
                    <p :class="['text-sm font-medium', slot.isFull ? 'text-red-600' : selectedTimeSlot?.id === slot.id ? 'text-blue-700' : 'text-slate-700']">{{ slot.label }}</p>
                    <p :class="['text-xs mt-0.5', slot.isFull ? 'text-red-400' : selectedTimeSlot?.id === slot.id ? 'text-blue-500' : 'text-slate-400']">{{ slot.start }}-{{ slot.end }}</p>
                    <p v-if="slot.isFull" class="text-xs text-red-500 mt-1 font-medium">마감</p>
                    <p v-else class="text-xs text-green-600 mt-1">{{ slot.available }}자리</p>
                  </button>
                </div>
              </div>

              <!-- 광고 정보 입력 -->
              <div class="bg-white rounded-2xl border border-slate-200 p-6">
                <h2 class="text-lg font-semibold text-slate-800 mb-4">3. 광고 정보 입력</h2>

                <div class="space-y-4">
                  <!-- 이미지 업로드 -->
                  <div>
                    <label class="block text-sm font-medium text-slate-600 mb-2">광고 이미지 (16:9 권장)</label>
                    <div
                      class="relative border-2 border-dashed rounded-xl overflow-hidden transition"
                      :class="imageUrl ? 'border-blue-300' : 'border-slate-200 hover:border-blue-300'"
                    >
                      <div v-if="imageUrl" class="relative aspect-video">
                        <img :src="imageUrl" alt="광고 이미지" class="w-full h-full object-cover"/>
                        <button
                          @click="removeImage"
                          class="absolute top-2 right-2 w-8 h-8 bg-black/50 text-white rounded-full flex items-center justify-center hover:bg-black/70 transition"
                        >
                          <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12"/>
                          </svg>
                        </button>
                      </div>
                      <label v-else class="flex flex-col items-center justify-center py-12 cursor-pointer">
                        <svg class="w-12 h-12 text-slate-300 mb-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z"/>
                        </svg>
                        <span class="text-sm text-slate-500">이미지를 선택하세요</span>
                        <span class="text-xs text-slate-400 mt-1">16:9 비율 권장 (예: 1920x1080)</span>
                        <input
                          type="file"
                          accept="image/*"
                          @change="handleImageUpload"
                          class="hidden"
                        />
                      </label>
                    </div>
                    <div v-if="uploading" class="mt-2 flex items-center gap-2 text-sm text-blue-600">
                      <div class="spinner-sm"></div>
                      <span>업로드 중...</span>
                    </div>
                  </div>

                  <div>
                    <label class="block text-sm font-medium text-slate-600 mb-2">광고 제목</label>
                    <input
                      v-model="adTitle"
                      type="text"
                      maxlength="30"
                      placeholder="예: 강남 최고의 프리미엄 주거단지"
                      class="w-full px-4 py-3 border border-slate-200 rounded-xl focus:border-blue-500 focus:ring-2 focus:ring-blue-500/20 outline-none"
                    />
                    <p class="text-xs text-slate-400 mt-1">{{ adTitle.length }}/30자</p>
                  </div>

                  <div>
                    <label class="block text-sm font-medium text-slate-600 mb-2">광고 부제목 (선택)</label>
                    <input
                      v-model="adSubtitle"
                      type="text"
                      maxlength="50"
                      placeholder="예: 특별 분양 혜택 진행 중"
                      class="w-full px-4 py-3 border border-slate-200 rounded-xl focus:border-blue-500 focus:ring-2 focus:ring-blue-500/20 outline-none"
                    />
                    <p class="text-xs text-slate-400 mt-1">{{ adSubtitle.length }}/50자</p>
                  </div>

                  <div>
                    <label class="block text-sm font-medium text-slate-600 mb-2">지역 태그 (선택)</label>
                    <input
                      v-model="adRegion"
                      type="text"
                      maxlength="20"
                      placeholder="예: 서울 강남"
                      class="w-full px-4 py-3 border border-slate-200 rounded-xl focus:border-blue-500 focus:ring-2 focus:ring-blue-500/20 outline-none"
                    />
                    <p class="text-xs text-slate-400 mt-1">{{ adRegion.length }}/20자</p>
                  </div>
                </div>
              </div>

              <!-- 미리보기 -->
              <div v-if="selectedCampaign && selectedTimeSlot" class="bg-white rounded-2xl border border-slate-200 p-6">
                <h2 class="text-lg font-semibold text-slate-800 mb-4">4. 광고 미리보기</h2>

                <!-- 인앱 광고 스타일 미리보기 -->
                <div class="relative rounded-2xl overflow-hidden" style="height: 160px;">
                  <div
                    class="absolute inset-0"
                    :style="{
                      backgroundImage: imageUrl
                        ? `linear-gradient(105deg, rgba(0,0,0,0.85) 0%, rgba(0,0,0,0.5) 50%, rgba(0,0,0,0) 100%), url('${imageUrl}')`
                        : 'linear-gradient(135deg, #f97316 0%, #ea580c 100%)',
                      backgroundSize: 'cover',
                      backgroundPosition: 'center'
                    }"
                  ></div>
                  <div class="relative h-full p-5 flex flex-col justify-between text-white">
                    <div class="flex items-start justify-between">
                      <div v-if="adRegion" class="px-2 py-1 text-xs font-bold rounded-md bg-white/20 backdrop-blur-sm border border-white/30">
                        {{ adRegion }}
                      </div>
                      <div class="px-2 py-1 text-xs font-bold rounded-md bg-blue-500">
                        AD
                      </div>
                    </div>
                    <div>
                      <h3 class="text-lg font-bold">{{ adTitle || selectedCampaign.propertyName }}</h3>
                      <p v-if="adSubtitle" class="text-sm opacity-90 mt-1">{{ adSubtitle }}</p>
                      <div class="flex items-center gap-1 mt-2 opacity-90">
                        <span class="text-sm font-semibold">자세히 보기</span>
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5">
                          <path d="M9 18l6-6-6-6"/>
                        </svg>
                      </div>
                    </div>
                  </div>
                </div>

                <!-- 노출 정보 -->
                <div class="mt-4 p-4 bg-blue-50 rounded-xl">
                  <div class="flex items-center gap-2 text-blue-700">
                    <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"/>
                    </svg>
                    <span class="font-medium">노출 시간: {{ selectedTimeSlot.label }} ({{ selectedTimeSlot.start }} - {{ selectedTimeSlot.end }})</span>
                  </div>
                  <p class="text-sm text-blue-600 mt-1">승인 후 1주일간 현장 목록 상단에 노출됩니다.</p>
                </div>
              </div>

              <!-- 신청 버튼 -->
              <button
                v-if="canSubmit"
                @click="handleSubmit"
                :disabled="submitting"
                class="w-full py-4 bg-blue-600 text-white font-semibold rounded-xl hover:bg-blue-700 transition disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {{ submitting ? '신청 중...' : '광고 신청하기 (무료)' }}
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  </PartnersLayout>
</template>

<script>
import { ref, computed, onMounted } from 'vue'
import { useRouter } from 'vue-router'
import PartnersLayout from '@/components/partners/common/PartnersLayout.vue'
import PartnersHeader from '@/components/partners/common/PartnersHeader.vue'
import { useAuth } from '@/composables/common/useAuth'
import { useListAd } from '@/composables/main/useListAd'
import { collection, query, where, getDocs } from 'firebase/firestore'
import { db, storage } from '@/firebase/firebase'
import { ref as storageRef, uploadBytes, getDownloadURL } from 'firebase/storage'

export default {
  name: 'ListAdPurchaseView',
  components: {
    PartnersLayout,
    PartnersHeader
  },
  setup() {
    const router = useRouter()
    const { user, userProfile } = useAuth()
    const { TIME_SLOTS, createListAd, checkExistingListAd, getAvailableSlots } = useListAd()

    const loading = ref(true)
    const loadingSlots = ref(true)
    const campaigns = ref([])
    const selectedCampaign = ref(null)
    const selectedTimeSlot = ref(null)
    const adTitle = ref('')
    const adSubtitle = ref('')
    const adRegion = ref('')
    const imageUrl = ref('')
    const uploading = ref(false)
    const submitting = ref(false)
    const hasActiveListAd = ref(false)
    const availableSlots = ref([])

    const canSubmit = computed(() => {
      return selectedCampaign.value &&
             selectedTimeSlot.value &&
             !selectedTimeSlot.value.isFull &&
             imageUrl.value &&
             adTitle.value.trim()
    })

    // 이미지 업로드
    const handleImageUpload = async (e) => {
      const file = e.target.files[0]
      if (!file) return

      if (!file.type.startsWith('image/')) {
        alert('이미지 파일만 업로드 가능합니다.')
        return
      }

      if (file.size > 5 * 1024 * 1024) {
        alert('파일 크기는 5MB 이하여야 합니다.')
        return
      }

      uploading.value = true

      try {
        const fileName = `listads/${user.value.uid}/${Date.now()}_${file.name}`
        const fileRef = storageRef(storage, fileName)
        await uploadBytes(fileRef, file)
        imageUrl.value = await getDownloadURL(fileRef)
      } catch (err) {
        console.error('이미지 업로드 실패:', err)
        alert('이미지 업로드에 실패했습니다.')
      } finally {
        uploading.value = false
      }
    }

    const removeImage = () => {
      imageUrl.value = ''
    }

    // 잔여 슬롯 로드
    const loadAvailableSlots = async () => {
      loadingSlots.value = true
      try {
        availableSlots.value = await getAvailableSlots()
      } catch (err) {
        console.error('슬롯 로드 실패:', err)
        availableSlots.value = TIME_SLOTS.map(slot => ({
          ...slot,
          currentCount: 0,
          available: 5,
          isFull: false
        }))
      } finally {
        loadingSlots.value = false
      }
    }

    // 활성 캠페인 로드
    const loadCampaigns = async () => {
      if (!user.value) return

      try {
        const q = query(
          collection(db, 'campaigns'),
          where('partnerId', '==', user.value.uid),
          where('status', '==', 'active')
        )
        const snapshot = await getDocs(q)

        campaigns.value = snapshot.docs.map(doc => ({
          id: doc.id,
          ...doc.data()
        })).sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt))

        // 캠페인이 하나면 자동 선택
        if (campaigns.value.length === 1) {
          selectedCampaign.value = campaigns.value[0]
        }
      } catch (err) {
        console.error('캠페인 로드 실패:', err)
      }
    }

    const handleSubmit = async () => {
      if (!canSubmit.value || submitting.value) return

      // 다시 한번 확인
      const exists = await checkExistingListAd(user.value.uid)
      if (exists) {
        alert('이미 등록된 광고가 있습니다.\n기존 광고가 만료되거나 삭제된 후 새로운 광고를 신청해주세요.')
        hasActiveListAd.value = true
        return
      }

      if (!confirm('광고를 신청하시겠습니까?\n관리자 승인 후 1주일간 현장 목록 상단에 노출됩니다.')) {
        return
      }

      submitting.value = true

      try {
        await createListAd({
          partnerId: user.value.uid,
          partnerEmail: user.value.email,
          partnerDisplayName: userProfile.value?.name || user.value.email,
          propertyId: selectedCampaign.value.propertyId,
          propertyName: selectedCampaign.value.propertyName,
          campaignId: selectedCampaign.value.id,
          campaignCode: selectedCampaign.value.code,
          imageUrl: imageUrl.value,
          title: adTitle.value.trim(),
          subtitle: adSubtitle.value.trim(),
          region: adRegion.value.trim(),
          timeSlot: {
            id: selectedTimeSlot.value.id,
            label: selectedTimeSlot.value.label,
            start: selectedTimeSlot.value.start,
            end: selectedTimeSlot.value.end,
            icon: selectedTimeSlot.value.icon
          }
        })

        alert('광고 신청이 완료되었습니다!\n관리자 승인 후 1주일간 현장 목록 상단에 노출됩니다.')

        router.push('/partners/listad/my')
      } catch (err) {
        console.error('광고 신청 실패:', err)
        alert('신청 실패: ' + err.message)
      } finally {
        submitting.value = false
      }
    }

    onMounted(async () => {
      loading.value = true

      // 잔여 슬롯 로드
      await loadAvailableSlots()

      // 기존 광고 확인
      if (user.value) {
        hasActiveListAd.value = await checkExistingListAd(user.value.uid)
      }

      // 캠페인 로드
      if (!hasActiveListAd.value) {
        await loadCampaigns()
      }

      loading.value = false
    })

    return {
      loading,
      loadingSlots,
      campaigns,
      selectedCampaign,
      selectedTimeSlot,
      availableSlots,
      adTitle,
      adSubtitle,
      adRegion,
      imageUrl,
      uploading,
      canSubmit,
      submitting,
      hasActiveListAd,
      userProfile,
      handleImageUpload,
      removeImage,
      handleSubmit
    }
  }
}
</script>

<style scoped>
@reference "tailwindcss";

.spinner {
  @apply w-10 h-10 border-4 border-blue-600 border-t-transparent rounded-full animate-spin;
}

.spinner-sm {
  @apply w-5 h-5 border-2 border-blue-600 border-t-transparent rounded-full animate-spin;
}
</style>
